import { useState, useRef, useEffect } from 'react';
import { Button, TextField, Typography } from '@hid-galaxy-ui/galaxy-react';
import { Field, Form, Formik } from 'formik';
import {
  NotificationError,
  NotificationSuccess,
} from '../../../../utils/notification';
import {
  ResendOtp,
  validateEmailOtp,
} from '../../../../services/app-user/app-user';
import { useHistory } from 'react-router-dom';
import buildPATHS from '../../../../utils/paths';
import styles from './OtpValidationForm.module.scss';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';
import cx from 'classnames';
import { APP_TYPE, RESEND_OTP_TIME } from '../../constant';
import { getTranslation } from '../../../../utils/functions';
import LodashUtils from '../../../../utils/lodashUtils';
import useTimer from '../../../../hooks/useTimer';
import { ONLY_NUMBER_REGEX } from '../../../../constants/regex';
import { IAppProps } from '../../../../App';
import BoxLogo from '../../../../components/BoxLogo/BoxLogo';
import {
  IResendOtpParams,
  IValidateEmailOtpParams,
} from '../../../../services/app-user/IAppUser';
import {
  getProjectKey,
  getUrlLastPathValue,
} from '../../../../environments/url-helpers';

interface IOtpValidationFormProps extends IAppProps {
  registeredEmail: string;
  handleOtpFormSubmit?: (values: IValidateEmailOtpParams) => void;
  handleResendOtpFormSubmit?: () => void;
  showLogo?: boolean;
}

const OtpValidationForm = (props: IOtpValidationFormProps) => {
  const {
    registeredEmail,
    setToasts,
    toasts,
    handleOtpFormSubmit,
    handleResendOtpFormSubmit,
    showLogo = true,
  } = props;
  const [otp, setOtp] = useState(['', '', '', '']);

  const otpRefs = useRef([]);
  const history = useHistory();
  const PATHS = buildPATHS();
  const projectKey = getProjectKey();
  const { time, isActive, startTimer } = useTimer(RESEND_OTP_TIME);

  useEffect(() => {
    startTimer();
  }, []);

  const handleOtpChange = (index, e) => {
    const value = e.target.value;
    if (ONLY_NUMBER_REGEX.test(value) || value === '') {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value !== '' && index < otpRefs.current.length - 1) {
        otpRefs.current[index + 1].focus();
      }
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && otp[index] === '' && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleResendOtp = async () => {
    if (handleResendOtpFormSubmit) {
      handleResendOtpFormSubmit();
      return;
    }

    try {
      const payload: IResendOtpParams = {
        email: registeredEmail,
      };
      const response = await ResendOtp(payload);
      setToasts([...toasts, ...NotificationSuccess('RESEND_OTP_SUCCESS')]);
      startTimer();
    } catch (error) {
      setToasts([...toasts, ...NotificationError('RESEND_OTP_ERROR')]);
    }
  };

  const handleOtpSubmit = async (values) => {
    let payload: IValidateEmailOtpParams = {
      otp: otp.join(''),
      email: registeredEmail,
    };

    if (handleOtpFormSubmit) {
      handleOtpFormSubmit(payload);
      return;
    }
    try {
      if (projectKey) {
        payload.projectHash = projectKey as string;
      }

      const response = await validateEmailOtp(payload);
      setToasts([
        ...toasts,
        ...NotificationSuccess(
          response?.statusReason || 'ACCOUNT_ACTIVATED_SUCCESS',
        ),
      ]);
      history.push(PATHS.account_register_success);
    } catch (error) {
      setToasts([
        ...toasts,
        ...NotificationError(
          error?.response?.data?.statusReason || 'INVALID_OTP',
        ),
      ]);
    }
  };

  return (
    <Formik
      initialValues={{
        otp: '',
      }}
      onSubmit={(values) => handleOtpSubmit(values)}
    >
      {({ submitForm, errors }) => (
        <>
          <Form className={cx('hid-spacing__px-03', 'register-form')}>
            {showLogo && <BoxLogo />}

            <Typography
              className={styles.otpMsg}
              variant={TypographyVariantEnum.H2}
            >
              {getTranslation('ENTER_OTP')}
            </Typography>

            <div className={styles.otpWrapper}>
              {otp.map((value, index) => (
                <TextField
                  id={`otp-${index}`}
                  name={`otp-${index}`}
                  key={index}
                  type="text"
                  maxLength={1}
                  value={value}
                  onChange={(e) => handleOtpChange(index, e)}
                  inputProps={{
                    className: 'otp-input',
                    ref: (el) => (otpRefs.current[index] = el),
                    onKeyDown: (e) => handleOtpKeyDown(index, e),
                  }}
                />
              ))}
            </div>

            <div className="hid-flex hid-flex-row hid-flex-jc-between hid-text-center hid-spacing__mt-07">
              <Button
                onClick={() => handleResendOtp()}
                variant="secondary"
                label={
                  isActive
                    ? `${getTranslation('RESEND_OTP')} in ${time} Sec`
                    : getTranslation('RESEND_OTP')
                }
                disabled={isActive}
              />
              <Button
                disabled={LodashUtils.isAnyValueEmptyInArray(otp)}
                label={getTranslation('VALIDATE_OTP')}
                type="submit"
              />
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
};

export default OtpValidationForm;
